import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Search, Phone, Mail, MapPin, Package, ShoppingBag, Star, Heart, Store, ShieldCheck, User as UserIcon, Copy, Trash2, Pencil, UserCog } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "../../lib/money";

const ROLE_COLORS = {
  admin: "bg-[#0033FF] text-white",
  seller: "bg-black text-white",
  customer: "bg-neutral-200 text-black",
};

const STATUS_COLORS = {
  placed: "bg-[#0033FF] text-white",
  shipped: "bg-black text-white",
  delivered: "bg-[#00A355] text-white",
  cancelled: "bg-[#FF2A00] text-white",
};

function copy(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* ignore */ }
}

function debounce(fn, ms = 300) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setUsers(data);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const debouncedSearch = useMemo(() => debounce((v) => load(v), 350), []);
  const onSearch = (v) => { setQ(v); debouncedSearch(v); };

  const setRole = async (id, role) => {
    await api.put(`/admin/users/${id}/role`, { role });
    toast.success("Role updated");
    load(q);
    if (detailId === id) openDetail(id);
  };
  const setSellerStatus = async (id, status) => {
    await api.put(`/admin/users/${id}/seller-status`, { seller_status: status });
    toast.success("Seller status updated");
    load(q);
    if (detailId === id) openDetail(id);
  };

  const openDetail = async (id) => {
    setDetailId(id);
    setDetail(null);
    setTab("overview");
    try {
      const { data } = await api.get(`/admin/users/${id}/details`);
      setDetail(data);
    } catch {
      toast.error("Could not load details");
    }
  };

  const closeDetail = () => { setDetailId(null); setDetail(null); };

  return (
    <div>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Users</h1>
          <p className="mt-2 text-sm text-neutral-600">Search by name, email, user ID, or WhatsApp. Click any row for full profile + order history.</p>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="admin-user-search"
            value={q}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search users…"
            className="pl-9 h-11 rounded-none border-black"
          />
        </div>
      </div>

      <div className="mt-6 text-xs uppercase tracking-widest text-neutral-500">
        {loading ? "Searching…" : `${users.length} result${users.length === 1 ? "" : "s"}`}
      </div>

      <div className="mt-3 border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {["User", "Email", "Role", "Seller", "Joined", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.user_id}
                data-testid={`admin-user-${u.user_id}`}
                className="border-b border-neutral-200 last:border-b-0 hover:bg-neutral-50 cursor-pointer"
              >
                <td className="px-4 py-3" onClick={() => openDetail(u.user_id)}>
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-8 h-8 object-cover border border-neutral-200 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 grid place-items-center bg-neutral-200 rounded-full text-xs font-bold">
                        {(u.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold underline underline-offset-4 decoration-dotted">{u.name}</div>
                      <div className="text-[11px] text-neutral-500">{u.user_id.slice(-12)}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3" onClick={() => openDetail(u.user_id)}>{u.email}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                    <SelectTrigger className="rounded-none border-black h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none border-black">
                      <SelectItem value="customer">customer</SelectItem>
                      <SelectItem value="seller">seller</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {u.role === "seller" ? (
                    <Select value={u.seller_status || "pending"} onValueChange={(v) => setSellerStatus(u.user_id, v)}>
                      <SelectTrigger data-testid={`seller-status-${u.user_id}`} className="rounded-none border-black h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none border-black">
                        <SelectItem value="pending">pending</SelectItem>
                        <SelectItem value="approved">approved</SelectItem>
                        <SelectItem value="rejected">rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-neutral-600" onClick={() => openDetail(u.user_id)}>
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Button
                    data-testid={`view-user-${u.user_id}`}
                    onClick={() => openDetail(u.user_id)}
                    variant="outline"
                    className="rounded-none border-black h-8 px-3 text-xs"
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="rounded-none border-black max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">User profile</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              Complete record across orders, products, reviews, and wishlist.
            </DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="py-20 text-center text-neutral-500">Loading…</div>
          ) : (
            <UserDetail detail={detail} tab={tab} setTab={setTab} onCopy={copy} refresh={() => openDetail(detailId)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------- detail view --------------------

function UserDetail({ detail, tab, setTab, onCopy, refresh }) {
  const u = detail.user;
  const s = detail.stats;

  const tabs = [
    { id: "overview", label: "Overview", count: null },
    { id: "buyer", label: "Orders placed", count: s.orders_as_buyer },
    { id: "seller", label: "Orders fulfilled", count: s.orders_as_seller },
    { id: "products", label: "Products listed", count: s.products_listed },
    { id: "reviews", label: "Reviews", count: s.reviews_written },
    { id: "wishlist", label: "Wishlist", count: detail.wishlist.length },
    { id: "deleted", label: "Recently deleted", count: null },
  ];

  return (
    <div>
      {/* Profile header */}
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start border border-neutral-200 p-5">
        {u.picture ? (
          <img src={u.picture} alt={u.name} className="w-20 h-20 object-cover border border-neutral-200 rounded-full" />
        ) : (
          <div className="w-20 h-20 grid place-items-center bg-neutral-200 rounded-full text-2xl font-black">
            {(u.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-black text-2xl">{u.name}</h3>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${ROLE_COLORS[u.role] || ""}`}>
              {u.role}{u.role === "seller" && u.seller_status ? ` · ${u.seller_status}` : ""}
            </span>
            <UserEditButton user={u} shopName={detail.shop_name} onSaved={refresh} />
            <ImpersonateButton user={u} />
          </div>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-neutral-500" />
              <a href={`mailto:${u.email}`} className="hover:text-[#0033FF]">{u.email}</a>
              <button onClick={() => onCopy(u.email)} className="text-neutral-500 hover:text-black"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            {u.whatsapp ? (
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-neutral-500" />
                <span>{u.whatsapp}</span>
                <a
                  href={`https://wa.me/${String(u.whatsapp).replace(/\D+/g, "")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[#25D366] text-xs font-semibold underline"
                >WhatsApp</a>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-neutral-400">
                <Phone className="w-3.5 h-3.5" />
                <span className="text-xs italic">No WhatsApp on file</span>
              </div>
            )}
            {detail.shop_name && (
              <div className="flex items-center gap-2">
                <Store className="w-3.5 h-3.5 text-neutral-500" />
                <span>Shop: <b>{detail.shop_name}</b></span>
              </div>
            )}
            <div className="flex items-center gap-2 text-neutral-500">
              <UserIcon className="w-3.5 h-3.5" />
              <span className="text-xs">ID: {u.user_id}</span>
              <button onClick={() => onCopy(u.user_id)} className="text-neutral-500 hover:text-black"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex items-center gap-2 text-neutral-500 text-xs">
              Joined {new Date(u.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
        {[
          { k: "Spent", v: fmt(s.buyer_spend), icon: ShoppingBag },
          { k: "Orders placed", v: s.orders_as_buyer, icon: ShoppingBag },
          { k: "Revenue earned", v: fmt(s.seller_revenue), icon: Package },
          { k: "Orders fulfilled", v: s.orders_as_seller, icon: Package },
        ].map((x) => (
          <div key={x.k} className="border border-neutral-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
              <x.icon className="w-3 h-3" /> {x.k}
            </div>
            <div className="font-display font-black text-xl mt-1">{x.v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-neutral-200 flex flex-wrap gap-x-4 gap-y-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`detail-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`pb-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
            }`}
          >
            {t.label}{t.count !== null ? ` (${t.count})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-4">
        {tab === "overview" && (
          <div className="text-sm text-neutral-600">
            Use the tabs above to drill into every record the system has on this user.
          </div>
        )}

        {tab === "buyer" && (
          <div className="space-y-3">
            {detail.orders_as_buyer.length === 0 && <EmptyNote label="No orders placed." />}
            {detail.orders_as_buyer.map((o) => (
              <OrderRow key={o.order_id} o={o} mode="buyer" onChange={refresh} />
            ))}
          </div>
        )}

        {tab === "seller" && (
          <div className="space-y-3">
            {detail.orders_as_seller.length === 0 && <EmptyNote label="No orders fulfilled." />}
            {detail.orders_as_seller.map((o) => (
              <OrderRow key={o.order_id} o={o} mode="seller" sellerId={u.user_id} onChange={refresh} />
            ))}
          </div>
        )}

        {tab === "deleted" && (
          <DeletedOrdersTab userId={u.user_id} refresh={refresh} />
        )}

        {tab === "products" && (
          <div className="space-y-2">
            {detail.products.length === 0 && <EmptyNote label="No products listed." />}
            {detail.products.map((p) => (
              <div key={p.product_id} className="flex gap-3 items-center border border-neutral-200 p-3">
                {p.images?.[0] && <img src={p.images[0].startsWith("http") ? p.images[0] : `${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`} alt="" className="w-14 h-14 object-cover border border-neutral-200" />}
                <div className="flex-1">
                  <div className="font-display font-bold">{p.title}</div>
                  <div className="text-xs text-neutral-500">{p.category} · Stock {p.stock} · Rating {p.rating || "—"} ({p.reviews_count || 0})</div>
                </div>
                <div className="font-display font-black">{fmt(p.price)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-3">
            {detail.reviews.length === 0 && <EmptyNote label="No reviews written." />}
            {detail.reviews.map((r) => (
              <div key={r.review_id} className="border border-neutral-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-black stroke-black" : "stroke-neutral-300"}`} />
                    ))}
                  </div>
                  <span className="text-xs text-neutral-500">on product {r.product_id.slice(-12)}</span>
                </div>
                <div className="text-sm">{r.comment}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "wishlist" && (
          <div className="space-y-2">
            {detail.wishlist.length === 0 && <EmptyNote label="Wishlist is empty." />}
            {detail.wishlist.map((p) => (
              <div key={p.product_id} className="flex gap-3 items-center border border-neutral-200 p-3">
                <Heart className="w-4 h-4 text-[#FF2A00]" />
                <div className="flex-1 font-semibold">{p.title}</div>
                <div className="font-display font-black">{fmt(p.price)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyNote({ label }) {
  return <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-4 text-center">{label}</div>;
}

function OrderRow({ o, mode, sellerId, onChange }) {
  const a = o.shipping_address || {};
  const items = mode === "seller" && sellerId ? o.items.filter((it) => it.seller_id === sellerId) : o.items;
  const rowTotal = mode === "seller" ? items.reduce((s, it) => s + it.price * it.quantity, 0) : o.total;

  const changeStatus = async (next) => {
    try {
      await api.put(`/admin/orders/${o.order_id}`, { status: next });
      toast.success(`Status → ${next}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async () => {
    const restore = window.confirm(
      `Delete order #${o.order_id.slice(-8)}?\n\nOK = also RESTORE the ordered units back into product stock.\nCancel = delete WITHOUT restoring stock.`
    );
    const confirmDelete = window.confirm("This permanently removes the order (a tombstone is kept for audit). Proceed?");
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/orders/${o.order_id}?restore_stock=${restore}`);
      toast.success("Order deleted" + (restore ? " · stock restored" : ""));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="border border-neutral-200 p-3 text-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold flex items-center gap-1.5">
            #{o.order_id.slice(-8)}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); navigator.clipboard.writeText(o.order_id); }}
              title={`Copy full id: ${o.order_id}`}
              className="text-[10px] text-neutral-400 hover:text-black font-mono normal-case font-normal"
            >
              copy full
            </button>
          </div>
          <div className="text-xs text-neutral-500">
            {new Date(o.created_at).toLocaleString()}
            {mode === "seller" && o.customer_email ? ` · ${o.customer_email}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
          <span className="font-display font-black">{fmt(rowTotal)}</span>
          <Select value={o.status} onValueChange={changeStatus}>
            <SelectTrigger className="rounded-none border-black h-8 w-28 text-xs" data-testid={`admin-status-${o.order_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-black">
              <SelectItem value="placed">placed</SelectItem>
              <SelectItem value="shipped">shipped</SelectItem>
              <SelectItem value="delivered">delivered</SelectItem>
              <SelectItem value="cancelled">cancelled</SelectItem>
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={del}
            data-testid={`admin-delete-order-${o.order_id}`}
            title="Delete order"
            className="h-8 w-8 inline-flex items-center justify-center border border-black hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {mode === "seller" && (
        <div className="mt-2 text-xs text-neutral-600 flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 mt-0.5" />
          <span>
            <b className="text-black">{a.full_name}</b> · {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.postal_code}, {a.country} · <b className="text-black">{a.phone}</b>
          </span>
        </div>
      )}
      <div className="mt-2 pt-2 border-t border-neutral-200 space-y-1">
        {items.map((it, idx) => (
          <div key={`${it.product_id}-${idx}`} className="flex justify-between text-xs">
            <span>{it.title} × {it.quantity}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}</span>
            <span className="font-semibold">{fmt(it.price * it.quantity)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function DeletedOrdersTab({ userId, refresh }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/deleted-orders?user_id=${encodeURIComponent(userId)}&limit=100`);
      setOrders(data.orders || []);
    } catch {
      toast.error("Couldn't load deleted orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  const restore = async (orderId, stockWasRestored) => {
    const reDec = stockWasRestored
      ? window.confirm(`Restore order ${orderId.slice(-8)}?\n\nOK = re-decrement stock (correct since stock was returned on delete).\nCancel = leave stock unchanged.`)
      : false;
    if (!stockWasRestored) {
      const okay = window.confirm(`Restore order ${orderId.slice(-8)}? (Stock will be left unchanged since the delete didn't restore stock.)`);
      if (!okay) return;
    }
    try {
      await api.post(`/admin/deleted-orders/${orderId}/restore?re_decrement_stock=${reDec}`);
      toast.success("Order restored");
      load();
      refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Loading deleted orders…</div>;
  if (orders.length === 0) return <EmptyNote label="No deleted orders for this user." />;

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500">
        Showing {orders.length} tombstone{orders.length === 1 ? "" : "s"}. Click <b>Restore</b> to bring an order back.
      </div>
      {orders.map((o) => (
        <div key={o.order_id} className="border-2 border-dashed border-[#FF2A00] bg-[#FFF5F2] p-3 text-sm" data-testid={`deleted-order-${o.order_id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-display font-bold flex items-center gap-1.5">
                #{o.order_id.slice(-8)}
                <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FF2A00] text-white">DELETED</span>
              </div>
              <div className="text-[11px] text-neutral-600 mt-0.5">
                Deleted {o.deleted_at ? new Date(o.deleted_at).toLocaleString() : "?"}
                {o.stock_restored ? " · stock was restored" : " · stock kept as-is"}
              </div>
              <div className="text-[11px] text-neutral-500">
                Originally placed {new Date(o.created_at).toLocaleString()} · total {fmt(o.total)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => restore(o.order_id, !!o.stock_restored)}
              data-testid={`restore-order-${o.order_id}`}
              className="h-9 px-3 bg-[#0033FF] hover:bg-[#0022CC] text-white text-xs font-semibold uppercase tracking-wider"
            >
              Restore
            </button>
          </div>
          <div className="mt-2 pt-2 border-t border-[#FF2A00]/30 space-y-1">
            {o.items.map((it, idx) => (
              <div key={`${it.product_id}-${idx}`} className="flex justify-between text-xs">
                <span>{it.title} × {it.quantity}</span>
                <span className="font-semibold">{fmt(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// -------------------- inline user edit --------------------

function UserEditButton({ user, shopName, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        data-testid={`user-edit-btn-${user.user_id}`}
        variant="outline"
        className="rounded-none border-black h-7 px-2 text-[10px] uppercase tracking-widest"
        onClick={() => setOpen(true)}
      >
        <Pencil className="w-3 h-3 mr-1" /> Edit
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-black max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">Edit user</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Admin can change any field. Email & slug must be unique.
            </DialogDescription>
          </DialogHeader>
          <UserEditForm
            user={user}
            shopName={shopName}
            onSaved={() => { setOpen(false); onSaved?.(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function UserEditForm({ user, shopName, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "customer",
    seller_status: user.seller_status || "",
    whatsapp: user.whatsapp || "",
    affiliate_slug: user.affiliate_slug || "",
    shop_name: shopName || "",
    picture: user.picture || "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.seller_status) payload.seller_status = null;
      if (!payload.affiliate_slug) payload.affiliate_slug = "";
      if (!payload.shop_name) payload.shop_name = null;
      if (!payload.picture) payload.picture = null;
      if (!payload.whatsapp) payload.whatsapp = null;
      await api.put(`/admin/users/${user.user_id}`, payload);
      toast.success("User updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="user-edit-form">
      <Field label="Name">
        <Input data-testid="ef-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-none border-black h-10" />
      </Field>
      <Field label="Email">
        <Input data-testid="ef-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-none border-black h-10" />
      </Field>
      <Field label="Role">
        <Select value={form.role} onValueChange={(v) => update("role", v)}>
          <SelectTrigger data-testid="ef-role" className="rounded-none border-black h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none border-black">
            <SelectItem value="customer">customer</SelectItem>
            <SelectItem value="seller">seller</SelectItem>
            <SelectItem value="admin">admin</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Seller status">
        <Select value={form.seller_status || "none"} onValueChange={(v) => update("seller_status", v === "none" ? "" : v)}>
          <SelectTrigger data-testid="ef-seller-status" className="rounded-none border-black h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none border-black">
            <SelectItem value="none">— none —</SelectItem>
            <SelectItem value="pending">pending</SelectItem>
            <SelectItem value="approved">approved</SelectItem>
            <SelectItem value="rejected">rejected</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="WhatsApp (digits only)">
        <Input data-testid="ef-whatsapp" value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} className="rounded-none border-black h-10" />
      </Field>
      <Field label="Affiliate slug">
        <Input data-testid="ef-slug" value={form.affiliate_slug} onChange={(e) => update("affiliate_slug", e.target.value.toLowerCase())} className="rounded-none border-black h-10 font-mono" />
      </Field>
      <Field label="Shop name (if seller)" full>
        <Input data-testid="ef-shop" value={form.shop_name} onChange={(e) => update("shop_name", e.target.value)} className="rounded-none border-black h-10" />
      </Field>
      <Field label="Picture URL" full>
        <Input data-testid="ef-picture" value={form.picture} onChange={(e) => update("picture", e.target.value)} className="rounded-none border-black h-10" />
      </Field>
      <div className="col-span-2 flex justify-end gap-2 mt-2">
        <Button type="button" variant="outline" className="rounded-none border-black h-10 px-4" onClick={onCancel}>Cancel</Button>
        <Button data-testid="user-edit-save" type="button" disabled={busy} onClick={save} className="rounded-none bg-black hover:bg-neutral-800 h-10 px-5">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
      <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">{label}</label>
      {children}
    </div>
  );
}

// -------------------- Sign-in-as-user (impersonation) --------------------

function ImpersonateButton({ user }) {
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    if (user.role === "admin") {
      toast.error("Cannot impersonate another admin");
      return;
    }
    const ok = window.confirm(
      `Sign in as ${user.name} (${user.email})?\n\n` +
      `• You'll see the site exactly as they do.\n` +
      `• Every action will be performed as this user.\n` +
      `• A red banner will stay on screen so you don't forget.\n` +
      `• Click "End session" any time to return to your admin account.\n` +
      `• Session expires in 4 hours.\n\n` +
      `This is logged in the admin audit trail.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.user_id}/impersonate`);
      toast.success(`Signed in as ${user.name}`);
      // Hard reload to root so the new session takes effect everywhere
      window.location.assign("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
      setBusy(false);
    }
  };

  if (user.role === "admin") return null;
  return (
    <Button
      data-testid={`user-impersonate-btn-${user.user_id}`}
      variant="outline"
      className="rounded-none border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white h-7 px-2 text-[10px] uppercase tracking-widest"
      onClick={start}
      disabled={busy}
      title="Sign in as this user"
    >
      <UserCog className="w-3 h-3 mr-1" />
      {busy ? "Switching…" : "Sign in as"}
    </Button>
  );
}

